import { useEffect, useRef, useState } from 'react';
import { InputField, PinCodeField } from '@/components/formParts';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { API_BASE_URL } from '@/config';
import { ClipLoader } from 'react-spinners';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

async function extractApiMessage(res: Response, fallback: string) {
  try {
    const j = await res.clone().json();
    const raw = (j?.message ?? j?.error ?? '').toString();
    if (raw) return raw;
  } catch {/* noop */}
  const t = (await res.text()) || '';
  return t || fallback;
}

const normalizeCode = (s: string) =>
  (s ?? '').normalize('NFKC').replace(/[^\d]/g, '').slice(0, 6);
// ---------- utils ----------
const zeroPad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const normalizeBirthday = (raw: string): string | null => {
  if (!raw) return null;
  const v = raw.trim();
  // 1990年01月01日
  const jp = v.match(/^(\d{4})\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*日$/);
  if (jp) {
    const y = Number(jp[1]), m = Number(jp[2]), d = Number(jp[3]);
    const dt = new Date(y, m - 1, d);
    if (dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d) {
      return `${y}-${zeroPad(m)}-${zeroPad(d)}`;
    }
    return null;
  }
  // 1990/01/01 or 1990-01-01
  const sep = v.match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})$/);
  if (sep) {
    const y = Number(sep[1]), m = Number(sep[2]), d = Number(sep[3]);
    const dt = new Date(y, m - 1, d);
    if (dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d) {
      return `${y}-${zeroPad(m)}-${zeroPad(d)}`;
    }
    return null;
  }
  // 19900101
  const plain = v.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (plain) {
    const y = Number(plain[1]), m = Number(plain[2]), d = Number(plain[3]);
    const dt = new Date(y, m - 1, d);
    if (dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d) {
      return `${y}-${zeroPad(m)}-${zeroPad(d)}`;
    }
    return null;
  }
  return null;
};
const normalizePhone = (raw: string) => (raw || '').replace(/[^\d]/g, '');

// ---------- schemas ----------
const identitySchema = z.object({
  email: z.string().min(1, { message: 'メールアドレスは必須です' }).email({ message: 'メールアドレス形式が不正です' }),
  birthday: z.string().min(1, { message: '生年月日は必須です' })
    .transform((v) => normalizeBirthday(v) ?? '')
    .refine((v) => /^\d{4}-\d{2}-\d{2}$/.test(v), { message: '日付形式が不正です（例：1990年01月01日）' }),
  phone: z.string().min(8, { message: '電話番号は必須です' })
    .transform((v) => normalizePhone(v))
    .refine((v) => /^\d{8,11}$/.test(v), { message: '電話番号は8〜11桁の数字で入力してください' }),
});
type IdentityFormData = z.infer<typeof identitySchema>;

let latestEmail = ''; // パスワード≠メールのチェック用（resolver内から参照）

const resetSchema = z.object({
  code: z.string().regex(/^\d{6}$/, { message: '認証コードは数字6桁で入力してください' }),
  password: z.string()
    .min(8, { message: '8文字以上で入力してください' })
    .refine((v) => /[a-z]/.test(v), { message: '英小文字を含めてください' })
    .refine((v) => /[A-Z]/.test(v), { message: '英大文字を含めてください' })
    .refine((v) => /\d/.test(v),   { message: '数字を含めてください' })
    .refine((v) => /[^\w\s]/.test(v), { message: '記号を含めてください' }),
  password_confirm: z.string().min(1, { message: 'パスワード確認は必須です' }),
}).superRefine((data, ctx) => {
  if (/(.)\1{7,}/.test(data.password)) {
    ctx.addIssue({ code: 'custom', message: '同一文字を8回以上連続で使用できません', path: ['password'] });
  }
  if (data.password !== data.password_confirm) {
    ctx.addIssue({ code: 'custom', message: 'パスワードが一致しません', path: ['password_confirm'] });
  }
  if (latestEmail && data.password === latestEmail) {
    ctx.addIssue({ code: 'custom', message: 'メールアドレスと異なるパスワードを設定してください', path: ['password'] });
  }
});
type ResetFormData = z.infer<typeof resetSchema>;

// 互換性のため（親の onComplete に渡す型）
export type RegisterEmailFormData = IdentityFormData & ResetFormData & {
  email_confirm?: string;
};

// ---------- Component ----------
type RegisterEmailFormProps = {
  onComplete: (data: RegisterEmailFormData) => void;
};

const RegisterEmailForm = ({ onComplete }: RegisterEmailFormProps) => {
  // Step1: 本人確認フォーム
  const idForm = useForm<IdentityFormData>({ mode: 'onTouched', resolver: zodResolver(identitySchema) });
  const { register: idRegister, handleSubmit: handleIdSubmit, formState: { errors: idErrors, isValid: idValid }, watch: idWatch } = idForm;

  // Step2: コード＋PWフォーム
  const resetForm = useForm<ResetFormData>({ mode: 'onTouched', resolver: zodResolver(resetSchema) });
  const { register: rsRegister, handleSubmit: handleRsSubmit, formState: { errors: rsErrors, isValid: rsValid }, setValue: rsSetValue, watch: rsWatch, trigger: rsTrigger, reset: rsReset } = resetForm;

  const [isLoading, setIsLoading] = useState(false);
  const [isRequested, setIsRequested] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [cooldown, setCooldown] = useState<number>(0);
  const [email, setEmail] = useState('');
  const [identityCache, setIdentityCache] = useState<IdentityFormData | null>(null);

  const refRequested = useRef<HTMLDivElement>(null);
  const refCompleted = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isRequested && refRequested.current) {
      refRequested.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isRequested]);
  useEffect(() => {
    if (isCompleted && refCompleted.current) {
      refCompleted.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isCompleted]);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const basic = () => {
    // ⚠️本番はフロントに直書きしない想定
    const username = '2chiiki-stg-wp';
    const password = 'DNt~VqC*N.-5';
    return btoa(`${username}:${password}`);
  };

  // ----- Step1: 本人確認（OKならコード送信） -----
  const onIdentity = async (d: IdentityFormData) => {
    setIsLoading(true);
    try {
      latestEmail = d.email;
      const res = await fetch(`${API_BASE_URL}/v1/user/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Basic ${basic()}` },
        // サーバ側：email/birthday/phone を突合し、OKならメールにコード送信
        body: JSON.stringify({ email: d.email, birthday: d.birthday, phone: d.phone ,flow:"password_change"}),
      });
      if (!res.ok) {
           alert(await extractApiMessage(res, '本人確認に失敗しました。入力内容を確認してください。'));
           return;
      }

      setEmail(d.email);
      setIdentityCache(d);
      setIsRequested(true);
      setCooldown(60);
      rsReset(); // コード/パスワードをリセット
    } catch (err: any) {
      alert('本人確認に失敗しました: ' + (err?.message ?? '不明なエラー'));
    } finally {
      setIsLoading(false);
    }
  };

  const onResend = async () => {
    if (cooldown > 0 || !identityCache) return;
    await onIdentity(identityCache); // 同じ入力で再送
  };

  // ----- Step2: コード検証＋パスワード更新 -----
  const onReset = async (d: ResetFormData) => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/v1/user/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${basic()}`
        },
        body: JSON.stringify({ email, code: d.code, new_password: d.password }),
      });
  
      if (!res.ok) {
        // サーバーの {message:"..."} or {error:"..."} を丁寧に拾う
        let msg = 'パスワード再設定に失敗しました';
        try {
          const j = await res.clone().json();
          msg = (j?.message ?? j?.error ?? msg).toString();
        } catch {
          const t = (await res.text()) || '';
          msg = t || msg;
        }
        alert(msg); // 例: 「前回と同じパスワードは設定できません」
        return;     // ここで終了
      }
  
      setIsCompleted(true);
    } catch (err) {
      alert('ネットワークエラーが発生しました。時間をおいて再度お試しください。');
    } finally {
      setIsLoading(false);
    }
  };
  

  const onRestart = () => {
    setIsRequested(false);
    setIsCompleted(false);
    setCooldown(0);
    setEmail('');
    setIdentityCache(null);
    latestEmail = '';
  };

  // パスワード強度の簡易表示
  const pw = rsWatch('password') ?? '';
  const pwRules = {
    len: pw.length >= 8,
    lower: /[a-z]/.test(pw),
    upper: /[A-Z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: /[^\w\s]/.test(pw),
    repeat: !/(.)\1{7,}/.test(pw),
  };
  const navigate = useNavigate();
  const handleGoNext = () => {
    // Router に basename（例: /user）が設定されていれば、/mypage は自動で /user/mypage になります
    const target = '/mypage';
  
    // ① 親ページごと遷移したい（iframe を抜けたい）場合は top を書き換える
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.assign(target); // 同一ドメイン前提
        return;
      }
    } catch (_) {
      // クロスオリジンで top 参照がブロックされた場合は iframe 内だけ遷移
    }
  
    // ② iframe 内だけで SPA 遷移
    navigate(target, { replace: true });
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* ------- ヘッダー＆案内（添付レイアウト準拠） ------- */}
      <h1 className="text-2xl font-bold mb-6">パスワードをお忘れの方へ</h1>
      <div className="border rounded-md p-6 bg-gray-50 text-sm mb-10">
        <p className="mb-3">
          ご本人様確認のため、下記の情報をご入力ください。<br />
          入力内容が確認できましたら、登録メールアドレス宛にパスワード再設定に関するご案内をお送りします。
        </p>
        <p className="text-xs leading-6">
          ※ご案内メールは「2chiiki@ana-akindo.co.jp」ドメインから送信されます。<br />
          受信制限をされている場合は、あらかじめドメイン解除をお願いいたします。
        </p>
      </div>

      {/* ------- STEP 1：本人確認 ------- */}
      {!isRequested && !isCompleted && (
        <form onSubmit={handleIdSubmit(onIdentity)} className="flex flex-col gap-8">
          <InputField
            label="メールアドレス（ご登録のメールアドレスをご入力ください）"
            type="email"
            register={idRegister('email')}
            errors={idErrors}
            hasRequiredLabel
          />
          <InputField
            label="生年月日（例：1990/01/01もしくは19900101）"
            type="text"
            placeholder="1990/01/01 もしくは 19900101"
            register={idRegister('birthday')}
            errors={idErrors}
            hasRequiredLabel
          />
          <InputField
            label="電話番号（例：0902345678）※ハイフン不要"
            type="tel"
            placeholder="09012345678"
            register={idRegister('phone')}
            errors={idErrors}
            hasRequiredLabel
          />

          <div className="flex justify-center mt-2">
            <button type="submit" className={`m-btn fill ${(!idValid || isLoading) ? 'disabled' : ''}`}>
              次へ
            </button>
          </div>
          {isLoading && <div className="flex justify-center"><ClipLoader loading={isLoading} /></div>}
        </form>
      )}

      {/* ------- STEP 2：コード入力＋PW再設定 ------- */}
      {isRequested && !isCompleted && (
        <form onSubmit={handleRsSubmit(onReset)} className="flex flex-col gap-8 mt-2">
          <div className="text-center bg-yellow-100 py-8 px-2 rounded-lg border border-yellow-300" ref={refRequested}>
            <p>
              <strong>{email}</strong> 宛に認証コードを送信しました。<br />
              メールに記載の認証コード（6桁）と新しいパスワードを入力してください。
            </p>
          </div>

          <div className="w-[25rem] mx-auto">
          {/* <PinCodeField
            label="認証コード"
            register={rsRegister('code')}
            errors={rsErrors}
            length={6}
            onChange={(value: string) => {
              const v = (value ?? '').replace(/\D/g, '').slice(0, 6);
              rsSetValue('code', v, { shouldValidate: true, shouldDirty: true });
            }}
            value={rsWatch('code') ?? ''}
          /> */}
              <InputField
                label="認証コード"
                type="tel"
                inputMode="numeric"
                autoComplete="one-time-code"
                {...rsRegister('code')}
                value={rsWatch('code') || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const v = (e.target.value || '').replace(/\D/g, '').slice(0, 6);
                  rsSetValue('code', v, { shouldValidate: true, shouldDirty: true });
                  // trigger('code'); // ここで即時バリデしたいなら有効化
                }}
              />
          </div>

          <div className="grid xmd:grid-cols-2 gap-x-4 gap-y-4">
            <InputField
              label="新しいパスワード"
              type="password"
              register={rsRegister('password')}
              errors={rsErrors}
              hasRequiredLabel
              onContextMenu={(e: any) => e.preventDefault()}
            />
            <InputField
              label="新しいパスワード（確認）"
              type="password"
              register={rsRegister('password_confirm')}
              errors={rsErrors}
              hasRequiredLabel
              onContextMenu={(e: any) => e.preventDefault()}
            />
          </div>

          <div className="text-gray-500 mt-2">
            <p><small>※8文字以上で「英大文字・小文字・数字・記号」を各1種類以上含めてください。同一文字8連続は不可。</small></p>
            <ul className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
              <li className={pwRules.len ? 'text-green-600' : 'text-gray-500'}>8文字以上</li>
              <li className={pwRules.lower ? 'text-green-600' : 'text-gray-500'}>小文字</li>
              <li className={pwRules.upper ? 'text-green-600' : 'text-gray-500'}>大文字</li>
              <li className={pwRules.digit ? 'text-green-600' : 'text-gray-500'}>数字</li>
              <li className={pwRules.symbol ? 'text-green-600' : 'text-gray-500'}>記号</li>
              <li className={pwRules.repeat ? 'text-green-600' : 'text-gray-500'}>同一文字8連続なし</li>
            </ul>
          </div>

          <div className="flex justify-center mt-2">
            <button type="submit" className={`m-btn fill ${(!rsValid || isLoading) ? 'disabled' : ''}`}>
              パスワードを再設定
            </button>
          </div>

          {isLoading && <div className="flex justify-center"><ClipLoader loading={isLoading} /></div>}

          <div className="flex items-center justify-center gap-4">
            <button type="button" className="m-btn outlined" onClick={onRestart}>はじめからやり直す</button>
            <button type="button" className="m-btn btn-small outlined" disabled={cooldown > 0 || isLoading} onClick={onResend}>
              コード再送{cooldown > 0 ? `（${cooldown}s）` : ''}
            </button>
          </div>
        </form>
      )}

      {/* ------- 完了 ------- */}
      {isCompleted && (
        <div className="text-center bg-yellow-100 py-8 px-2 rounded-lg border border-yellow-300 mt-8" ref={refCompleted}>
          <p>パスワードの再設定が完了しました。ログイン画面へお進みください。</p>
          <div className="flex justify-center mt-8">
          <button type="button" className="m-btn btn-small outlined" onClick={handleGoNext}>
            ログインへ
          </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterEmailForm;
